import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
	PlusIcon,
	PencilIcon,
	TrashIcon,
	ArrowUpIcon,
	ArrowDownIcon,
	EyeIcon,
	EyeSlashIcon
} from '@heroicons/react/24/outline';

import { API_URL } from '../../config/api';

const TestimonialManagement = () => {
	const [testimonials, setTestimonials] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [showModal, setShowModal] = useState(false);
	const [editingTestimonial, setEditingTestimonial] = useState(null);
	const [formData, setFormData] = useState({
		quote: '',
		name: '',
		company: '',
		active: true,
		order: 0
	});
	const [formErrors, setFormErrors] = useState({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		loadTestimonials();
	}, []);

	const loadTestimonials = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${API_URL}/api/content/testimonials/`);
			setTestimonials(Array.isArray(response.data) ? response.data : (response.data.results || []));
			setError('');
		} catch (err) {
			setError('Error al cargar testimonios');
			console.error('Error cargando testimonios:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setEditingTestimonial(null);
		setFormData({
			quote: '',
			name: '',
			company: '',
			active: true,
			order: testimonials.length
		});
		setFormErrors({});
		setShowModal(true);
	};

	const handleEdit = (testimonial) => {
		setEditingTestimonial(testimonial);
		setFormData({
			quote: testimonial.quote || '',
			name: testimonial.name || '',
			company: testimonial.company || '',
			active: testimonial.active !== undefined ? testimonial.active : true,
			order: testimonial.order || 0
		});
		setFormErrors({});
		setShowModal(true);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		setFormErrors({});
		setSaving(true);

		try {
			if (editingTestimonial) {
				await axios.put(`${API_URL}/api/content/testimonials/${editingTestimonial.id}/`, formData);
			} else {
				await axios.post(`${API_URL}/api/content/testimonials/`, formData);
			}

			await loadTestimonials();
			setShowModal(false);
		} catch (err) {
			if (err.response?.data) {
				setFormErrors(err.response.data);
			} else {
				setError('Error al guardar testimonio');
			}
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm('¿Estás seguro de eliminar este testimonio?')) return;

		try {
			await axios.delete(`${API_URL}/api/content/testimonials/${id}/`);
			await loadTestimonials();
		} catch (err) {
			setError('Error al eliminar testimonio');
		}
	};

	const handleToggleActive = async (testimonial) => {
		try {
			await axios.patch(`${API_URL}/api/content/testimonials/${testimonial.id}/`, {
				active: !testimonial.active
			});
			await loadTestimonials();
		} catch (err) {
			setError('Error al cambiar estado del testimonio');
		}
	};

	const handleOrderChange = async (testimonial, direction) => {
		const newOrder = direction === 'up' ? testimonial.order - 1 : testimonial.order + 1;
		try {
			await axios.patch(`${API_URL}/api/content/testimonials/${testimonial.id}/`, {
				order: newOrder
			});
			await loadTestimonials();
		} catch (err) {
			setError('Error al cambiar orden');
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div>
						<h1 className="text-3xl font-bold text-gray-900">Gestión de Testimonios</h1>
						<p className="mt-2 text-sm text-gray-600">
							Administra los testimonios de clientes
						</p>
					</div>
					<button
						onClick={handleCreate}
						className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-opacity"
						style={{ backgroundColor: '#12284C' }}
					>
						<PlusIcon className="h-5 w-5" />
						Crear Testimonio
					</button>
				</div>
			</div>

			{/* Error */}
			{error && (
				<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-red-800">{error}</p>
				</div>
			)}

			{/* Lista de Testimonios */}
			{loading ? (
				<div className="text-center py-12">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#12284C' }}></div>
					<p className="mt-4 text-gray-600">Cargando testimonios...</p>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Orden
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Nombre
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Empresa
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Testimonio
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Estado
									</th>
									<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
										Acciones
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{testimonials.length === 0 ? (
									<tr>
										<td colSpan="6" className="px-6 py-8 text-center text-gray-500">
											No hay testimonios creados
										</td>
									</tr>
								) : (
									testimonials.map((testimonial) => (
										<tr key={testimonial.id} className="hover:bg-gray-50">
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="flex items-center gap-2">
													<button
														onClick={() => handleOrderChange(testimonial, 'up')}
														className="p-1 hover:bg-gray-200 rounded"
														disabled={testimonial.order === 0}
													>
														<ArrowUpIcon className="h-4 w-4" />
													</button>
													<span className="text-sm font-medium text-gray-900">{testimonial.order}</span>
													<button
														onClick={() => handleOrderChange(testimonial, 'down')}
														className="p-1 hover:bg-gray-200 rounded"
													>
														<ArrowDownIcon className="h-4 w-4" />
													</button>
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm font-medium text-gray-900">{testimonial.name}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm text-gray-900">{testimonial.company || '-'}</div>
											</td>
											<td className="px-6 py-4">
												<div className="text-sm text-gray-900 truncate max-w-md">{testimonial.quote}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{testimonial.active ? (
													<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
														Activo
													</span>
												) : (
													<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
														Inactivo
													</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
												<div className="flex items-center justify-end gap-2">
													<button
														onClick={() => handleToggleActive(testimonial)}
														className="p-2 hover:bg-gray-100 rounded"
														title={testimonial.active ? 'Desactivar' : 'Activar'}
													>
														{testimonial.active ? (
															<EyeSlashIcon className="h-5 w-5 text-gray-600" />
														) : (
															<EyeIcon className="h-5 w-5 text-gray-600" />
														)}
													</button>
													<button
														onClick={() => handleEdit(testimonial)}
														className="p-2 hover:bg-gray-100 rounded"
														title="Editar"
													>
														<PencilIcon className="h-5 w-5" style={{ color: '#12284C' }} />
													</button>
													<button
														onClick={() => handleDelete(testimonial.id)}
														className="p-2 hover:bg-red-50 rounded"
														title="Eliminar"
													>
														<TrashIcon className="h-5 w-5 text-red-600" />
													</button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Modal Crear/Editar */}
			{showModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6">
							<div className="flex items-center justify-between mb-6">
								<h2 className="text-2xl font-bold text-gray-900">
									{editingTestimonial ? 'Editar Testimonio' : 'Crear Testimonio'}
								</h2>
								<button
									onClick={() => setShowModal(false)}
									className="text-gray-400 hover:text-gray-600"
								>
									<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							<form onSubmit={handleSave} className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Testimonio *
									</label>
									<textarea
										required
										value={formData.quote}
										onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
										rows={4}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
									/>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Nombre *
										</label>
										<input
											type="text"
											required
											value={formData.name}
											onChange={(e) => setFormData({ ...formData, name: e.target.value })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Empresa
										</label>
										<input
											type="text"
											value={formData.company}
											onChange={(e) => setFormData({ ...formData, company: e.target.value })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Orden
										</label>
										<input
											type="number"
											value={formData.order}
											onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
									<div>
										<label className="flex items-center space-x-2">
											<input
												type="checkbox"
												checked={formData.active}
												onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
												className="rounded border-gray-300"
												style={{ accentColor: '#12284C' }}
											/>
											<span className="text-sm font-medium text-gray-700">Activo</span>
										</label>
									</div>
								</div>

								{Object.keys(formErrors).length > 0 && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-4">
										{Object.entries(formErrors).map(([key, value]) => (
											<p key={key} className="text-sm text-red-800">
												{key}: {Array.isArray(value) ? value.join(', ') : value}
											</p>
										))}
									</div>
								)}

								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setShowModal(false)}
										className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
									>
										Cancelar
									</button>
									<button
										type="submit"
										disabled={saving}
										className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
										style={{ backgroundColor: '#12284C' }}
									>
										{saving ? 'Guardando...' : 'Guardar'}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default TestimonialManagement;

